export interface ILocalGrainProfileSyncRecord {
  grainId?: string | number;
  profileId?: string | number;
  profileType?: string;
  ownerUsername?: string;
  ownerOrgId?: string;
  syncStatus?: string;
  [key: string]: unknown;
}

export interface ISyncIdentity {
  username?: string;
  orgId?: string;
}

export function isSyncIdentityReady(
  loadedIdentity: ISyncIdentity | null | undefined,
  currentIdentity: ISyncIdentity
): boolean {
  if (!loadedIdentity?.username || !currentIdentity.username) return false;
  return loadedIdentity.username === currentIdentity.username
    && String(loadedIdentity.orgId || '') === String(currentIdentity.orgId || '');
}

export function selectPendingGrainProfilesForSync<T extends ILocalGrainProfileSyncRecord>(
  profiles: T[],
  identity: { username?: string; orgId?: string }
): T[] {
  if (!identity.username) return [];

  return (profiles || []).filter((profile) => {
    if (!profile.grainId || !profile.profileId) return false;
    if (!profile.syncStatus || profile.syncStatus === 'synced') return false;

    const isSupportedType = profile.profileType === 'CUSTOM'
      || profile.profileType === 'USER'
      || profile.profileType === 'ORG'
      || String(profile.profileId).startsWith('custom_')
      || profile.syncStatus === 'pending_delete';
    if (!isSupportedType) return false;

    if (profile.profileType === 'ORG' && profile.ownerOrgId && identity.orgId) {
      return profile.ownerOrgId === identity.orgId;
    }
    if (profile.ownerUsername) {
      return profile.ownerUsername === identity.username;
    }

    return profile.profileType === 'CUSTOM' || String(profile.profileId).startsWith('custom_');
  });
}

const profileIdentityKey = (profile: ILocalGrainProfileSyncRecord): string => (
  `${profile.grainId ?? ''}:${profile.profileId ?? ''}`
);

export function selectProfileSettingsReadyForSync<
  T extends { grainId?: string | number; profileId?: string | number }
>(settings: T[], profiles: ILocalGrainProfileSyncRecord[]): T[] {
  const blockedProfileIds = new Set(
    (profiles || [])
      .filter((profile) => profile.syncStatus && profile.syncStatus !== 'synced')
      .map(profileIdentityKey)
  );
  return (settings || []).filter((setting) => !blockedProfileIds.has(profileIdentityKey(setting)));
}

export function getNextActiveProfileId(params: {
  activeGrainId?: string | number;
  mutationGrainId?: string | number;
  currentProfileId?: string;
  nextProfiles: Array<{ profileId: string }>;
}): string {
  if (String(params.activeGrainId ?? '') !== String(params.mutationGrainId ?? '')) {
    return params.currentProfileId || '';
  }
  if (
    params.currentProfileId
    && params.nextProfiles.some((profile) => profile.profileId === params.currentProfileId)
  ) {
    return params.currentProfileId;
  }
  return params.nextProfiles[0]?.profileId || '';
}

export function shouldDeferCloudProfileDelete(params: {
  isOnline: boolean;
  profile: { profileId?: string | number; profileType?: string };
}): boolean {
  const isLocalOnly = params.profile.profileType === 'CUSTOM'
    || String(params.profile.profileId || '').startsWith('custom_');
  return !params.isOnline && !isLocalOnly;
}
